import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CallMetricsWidget } from '../components/dashboard/CallMetricsWidget';
import { InquirySummaryWidget } from '../components/dashboard/InquirySummaryWidget';
import { QuickActionsWidget } from '../components/dashboard/QuickActionsWidget';
import { ActivityFeedWidget } from '../components/dashboard/ActivityFeedWidget';
import { RecentMessagesWidget } from '../components/dashboard/RecentMessagesWidget';
import { UpcomingAppointmentsWidget } from '../components/dashboard/UpcomingAppointmentsWidget';
import { CasesWidget } from '../components/dashboard/CasesWidget';
import { ClientsWidget } from '../components/dashboard/ClientsWidget';
import { DocumentsWidget } from '../components/dashboard/DocumentsWidget';
import { InquiryQueueWidget } from '../components/dashboard/InquiryQueueWidget';
import { MessagesWidget } from '../components/dashboard/MessagesWidget';
import { RecentActivityWidget } from '../components/dashboard/RecentActivityWidget';
import { StatsWidget } from '../components/dashboard/StatsWidget';
import { DashboardService } from '../services/dashboardService';
import { DashboardLayout, DashboardWidget } from '../types/dashboard';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export function Dashboard() {
  const dashboardService = new DashboardService();
  const { data: userData, loading: userLoading } = useQuery<{ getCurrentUser: CurrentUser }>(GET_CURRENT_USER);
  
  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Available widget types for adding
  const availableWidgets = [
    { type: 'call-metrics', title: 'Call Metrics', description: 'Track call volume and performance' },
    { type: 'inquiry-summary', title: 'Inquiry Summary', description: 'Overview of recent inquiries' },
    { type: 'quick-actions', title: 'Quick Actions', description: 'Frequently used actions' },
    { type: 'activity-feed', title: 'Activity Feed', description: 'Recent system activities' },
    { type: 'recent-messages', title: 'Recent Messages', description: 'Latest client messages' },
    { type: 'upcoming-appointments', title: 'Upcoming Appointments', description: 'Your scheduled appointments' },
    { type: 'cases', title: 'Cases Overview', description: 'Active cases summary' },
    { type: 'clients', title: 'Clients Overview', description: 'Client statistics' },
    { type: 'documents', title: 'Documents', description: 'Recent document uploads' },
    { type: 'inquiry-queue', title: 'Inquiry Queue', description: 'Pending inquiries' },
    { type: 'messages', title: 'Messages', description: 'Message center' },
    { type: 'recent-activity', title: 'Recent Activity', description: 'Latest activities' },
    { type: 'stats', title: 'Statistics', description: 'Key performance metrics' },
  ];

  useEffect(() => {
    if (userData?.getCurrentUser) {
      loadDashboard();
    }
  }, [userData]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const userLayout = await dashboardService.getUserLayout(userData!.getCurrentUser.id);
      setLayout(userLayout);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const addWidget = async (type: string) => {
    if (!userData?.getCurrentUser) return;
    
    try {
      await dashboardService.addWidget(userData.getCurrentUser.id, type);
      await loadDashboard(); // Reload to get updated layout
      setShowAddWidget(false);
    } catch (error) {
      console.error('Error adding widget:', error);
    }
  };

  const removeWidget = async (widgetId: string) => {
    if (!userData?.getCurrentUser) return;
    
    try {
      await dashboardService.removeWidget(userData.getCurrentUser.id, widgetId);
      await loadDashboard(); // Reload to get updated layout
    } catch (error) {
      console.error('Error removing widget:', error);
    }
  };

  const resetLayout = async () => {
    if (!userData?.getCurrentUser) return;
    
    try {
      const defaultLayout = await dashboardService.resetToDefault(userData.getCurrentUser.id);
      setLayout(defaultLayout);
    } catch (error) {
      console.error('Error resetting layout:', error);
    }
  };

  const saveLayout = async () => {
    if (!layout || !userData?.getCurrentUser) return;
    
    try {
      await dashboardService.saveUserLayout(userData.getCurrentUser.id, layout);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    if (!isEditing) return;
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', widgetId);
    
    // Add visual feedback
    const target = e.target as HTMLElement;
    target.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    setDraggedWidget(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!layout || !draggedWidget) return;
    
    const draggedIndex = layout.widgets.findIndex(w => w.id === draggedWidget);
    if (draggedIndex === -1 || draggedIndex === dropIndex) return;

    // Reorder widgets
    const newWidgets = [...layout.widgets];
    const [draggedItem] = newWidgets.splice(draggedIndex, 1);
    newWidgets.splice(dropIndex, 0, draggedItem);

    // Update positions
    const updatedWidgets = newWidgets.map((widget, index) => ({
      ...widget,
      position: { 
        x: (index % 4) * 6, // 4 widgets per row, 6 columns each
        y: Math.floor(index / 4) * 4 // 4 rows high
      }
    }));

    setLayout({ ...layout, widgets: updatedWidgets });
    setDragOverIndex(null);
  };

  const renderWidget = (widget: DashboardWidget, index: number) => {
    const widgetContent = (() => {
      switch (widget.type) {
        case 'call-metrics':
          return <CallMetricsWidget className="h-full" />;
        case 'inquiry-summary':
          return <InquirySummaryWidget 
            widget={widget}
            isEditing={isEditing}
            onConfigChange={async (config: any) => {
              if (userData?.getCurrentUser) {
                await dashboardService.updateWidgetConfig(userData.getCurrentUser.id, widget.id, config);
                await loadDashboard();
              }
            }}
            onRemove={() => removeWidget(widget.id)}
          />;
        case 'quick-actions':
          return <QuickActionsWidget />;
        case 'activity-feed':
          return <ActivityFeedWidget 
            widget={widget}
            isEditing={isEditing}
            onConfigChange={async (config: any) => {
              if (userData?.getCurrentUser) {
                await dashboardService.updateWidgetConfig(userData.getCurrentUser.id, widget.id, config);
                await loadDashboard();
              }
            }}
            onRemove={() => removeWidget(widget.id)}
          />;
        case 'recent-messages':
          return <RecentMessagesWidget 
            widget={widget}
            isEditing={isEditing}
            onConfigChange={async (config: any) => {
              if (userData?.getCurrentUser) {
                await dashboardService.updateWidgetConfig(userData.getCurrentUser.id, widget.id, config);
                await loadDashboard();
              }
            }}
            onRemove={() => removeWidget(widget.id)}
          />;
        case 'upcoming-appointments':
          return <UpcomingAppointmentsWidget />;
        case 'cases':
        case 'cases-overview':
          return <CasesWidget />;
        case 'clients':
        case 'clients-overview':
          return <ClientsWidget />;
        case 'documents':
        case 'documents-overview':
          return <DocumentsWidget />;
        case 'inquiry-queue':
          return <InquiryQueueWidget />;
        case 'messages':
          return <MessagesWidget />;
        case 'recent-activity':
          return <RecentActivityWidget />;
        case 'stats':
          return <StatsWidget />;
        default:
          return (
            <Card className="h-full">
              <CardHeader>
                <CardTitle>{widget.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Widget type "{widget.type}" not implemented</p>
              </CardContent>
            </Card>
          );
      }
    })();

    return (
      <div 
        className={`relative h-full group widget-container ${
          dragOverIndex === index ? 'drag-over' : ''
        } ${draggedWidget === widget.id ? 'dragging' : ''}`}
        draggable={isEditing}
        onDragStart={(e) => handleDragStart(e, widget.id)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, index)}
      >
        {/* Professional drag handle */}
        {isEditing && (
          <div className="absolute top-3 left-3 z-20 cursor-move drag-handle opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              </svg>
            </div>
          </div>
        )}
        
        {/* Professional remove button */}
        {isEditing && (
          <div className="absolute top-3 right-3 z-20">
            <Button
              size="sm"
              variant="outline"
              className="h-6 w-6 p-0 bg-white hover:bg-red-50 border-gray-300 hover:border-red-300 text-gray-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
              onClick={(e) => {
                e.stopPropagation();
                removeWidget(widget.id);
              }}
              title="Remove widget"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        )}
        
        {widgetContent}
      </div>
    );
  };

  if (userLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={userData?.getCurrentUser} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={userData?.getCurrentUser} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">Error loading dashboard</h2>
            <Button onClick={loadDashboard} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const user = userData?.getCurrentUser;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-aalaw-charcoal">
              Welcome back, {user?.firstName}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          
          {/* Professional Dashboard Controls */}
          <div className="flex items-center gap-2">
            {isEditing && (
              <>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowAddWidget(true)}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Widget
                </Button>
                
                <Button
                  variant="outline"
                  onClick={resetLayout}
                  className="flex items-center gap-2 bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Layout
                </Button>
                
                <Button
                  onClick={saveLayout}
                  className="flex items-center gap-2 bg-aalaw-red hover:bg-aalaw-red-dark text-white"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Layout
                </Button>
              </>
            )}
            
            <Button
              variant={isEditing ? "default" : "outline"}
              onClick={() => setIsEditing(!isEditing)}
              className={`flex items-center gap-2 ${
                isEditing 
                  ? 'bg-aalaw-charcoal hover:bg-aalaw-charcoal-light text-white' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {isEditing ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Exit Edit Mode
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Customize
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Professional Edit Mode Banner */}
        {isEditing && (
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 text-sm">Dashboard Customization Mode</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Drag widgets to rearrange them or click the remove button to delete. Click "Save Layout" when finished.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AWS-style Dashboard Grid with Drag & Drop */}
        <div className={`dashboard-grid ${isEditing ? 'editing' : ''}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {layout.widgets.map((widget, index) => (
              <div 
                key={widget.id} 
                className={`${
                  widget.size.width === 6 ? 'lg:col-span-6' : 
                  widget.size.width === 4 ? 'lg:col-span-4' : 
                  widget.size.width === 3 ? 'lg:col-span-3' : 
                  'lg:col-span-12'
                }`}
                style={{ minHeight: `${widget.size.height * 80}px` }}
              >
                {renderWidget(widget, index)}
              </div>
            ))}
          </div>
        </div>

        {/* Professional Add Widget Modal */}
        {showAddWidget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-aalaw-charcoal">Add Widget</h3>
                <button
                  onClick={() => setShowAddWidget(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableWidgets.map((widgetType) => (
                    <button
                      key={widgetType.type}
                      className="text-left border border-gray-200 rounded-lg p-4 hover:border-aalaw-red hover:bg-red-50 transition-all group"
                      onClick={() => addWidget(widgetType.type)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gray-100 group-hover:bg-aalaw-red group-hover:text-white rounded flex items-center justify-center flex-shrink-0 transition-colors">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 mb-1 group-hover:text-aalaw-red transition-colors">{widgetType.title}</h4>
                          <p className="text-sm text-gray-600">{widgetType.description}</p>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-aalaw-red flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowAddWidget(false)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Professional Dashboard CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .dashboard-grid {
            position: relative;
          }
          
          .widget-container {
            height: 100%;
            border-radius: 8px;
            overflow: hidden;
            transition: all 200ms ease;
            cursor: default;
          }
          
          .widget-container:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }
          
          /* Drag and drop states */
          .widget-container[draggable="true"] {
            cursor: move;
          }
          
          .widget-container.dragging {
            opacity: 0.6;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
            z-index: 1000;
          }
          
          .widget-container.drag-over {
            border: 2px solid #C41E3A;
            background: rgba(196, 30, 58, 0.02);
          }
          
          .drag-handle {
            cursor: move;
            transition: opacity 200ms ease;
          }
          
          /* Professional edit mode highlighting */
          .dashboard-grid.editing .widget-container {
            border: 1px solid transparent;
            transition: all 200ms ease;
          }
          
          .dashboard-grid.editing .widget-container:hover:not(.dragging) {
            border-color: #e5e7eb;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          
          /* Smooth transitions for reordering */
          .dashboard-grid .grid > div {
            transition: transform 300ms ease;
          }
        `
      }} />
    </div>
  );
}
